import { Component, Input, OnInit } from '@angular/core';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';
import { WindowService } from '@cigna/shared/angular/core/window-util';

@Component({
  selector: 'cigna-resources-ui',
  templateUrl: './resources-ui.component.html',
  styleUrls: ['./resources-ui.component.scss'],
})
export class ResourcesUiComponent implements OnInit {
  @Input() pathname = '';
  @Input() isSecure = false;

  constructor(
    private analytics: ShellTrackHandlers,
    private window: WindowService,
  ) {}

  // eslint-disable-next-line sonarjs/cognitive-complexity
  ngOnInit() {
    const headerTitle = document.querySelector('h1,h2,h3');
    this.analytics.trackPageLoadOnInit(
      {} as any,
      this.pathname,
      'Resources',
      headerTitle && headerTitle.textContent !== null
        ? headerTitle.textContent.trim()
        : 'Untitled',
      'pv-Resources002',
      this.isSecure,
    );

    document.querySelectorAll('.cg-tab-list button').forEach((t) => {
      t.addEventListener('click', this.tabClick);
    });

    document.querySelectorAll('.toggle-faq').forEach((t, i) => {
      t.addEventListener('click', this.toggleAccordion);
      t.addEventListener('keydown', (event: KeyboardEvent) => {
        if (
          event.key === 'Enter' ||
          event.key === ' ' ||
          event.code === 'Space'
        ) {
          this.toggleAccordion(event);
        }
      });
      t.setAttribute('aria-expanded', 'false');
      t.setAttribute('role', 'button');

      const triggerId = `accordion-trigger-${i}`;
      const panelId = `accordion-panel-${i}`;

      t.setAttribute('id', triggerId);
      t.setAttribute('aria-controls', panelId);
      t.setAttribute('tabindex', '0');

      if (t.nextElementSibling) {
        const panel = t.nextElementSibling as HTMLDivElement;
        panel.setAttribute('id', panelId);
        panel.setAttribute('aria-labeledby', triggerId);
        panel.setAttribute('role', 'region');
      }
    });

    document.querySelectorAll('.resource-library li > a').forEach((t) => {
      t.addEventListener('click', this.trackCategoryImageClick);
    });

    document.querySelectorAll('.resource-library li > h3 > a').forEach((t) => {
      t.addEventListener('click', this.trackCategoryTextClick);
    });

    document.querySelectorAll('a.cg-track-resource-link').forEach((t) => {
      t.addEventListener('click', this.trackResourceLinkClick);
    });

    document.querySelectorAll('.cg-table-search-form > input').forEach((t) => {
      t.addEventListener('input', this.tableSearch);
    });

    document.querySelectorAll('table.cg-table-from-api').forEach((t) => {
      this.createTableFromApi(t as HTMLTableElement);
    });

    const openSelectVal = document.querySelector('.btn-openSelectVal');
    if (openSelectVal) {
      openSelectVal.addEventListener('click', () => {
        const s = document.querySelector('.selectval-container select');
        if (s) {
          const sVal = (s as HTMLSelectElement).options[
            (s as HTMLSelectElement).selectedIndex
          ].value;
          if (sVal) {
            this.window.open(sVal, '_blank');
          }
        }
      });
    }
  }

  trackCategoryImageClick = (event: MouseEvent) => {
    if (event.currentTarget) {
      const text = (
        (event.currentTarget as HTMLElement)
          .nextElementSibling as HTMLLinkElement
      ).textContent;
      if (text) {
        this.analytics.trackEventOther({}, text.trim(), 'ac-Resources003');
      }
    }
  };

  trackCategoryTextClick = (event: MouseEvent) => {
    if (event.currentTarget) {
      const text = (event.currentTarget as HTMLLinkElement).textContent;
      if (text) {
        this.analytics.trackEventOther({}, text.trim(), 'ac-Resources003');
      }
    }
  };

  trackResourceLinkClick = (event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      const text = target.textContent;
      const analtyicsType = target.dataset.prop13;
      if (text && analtyicsType) {
        this.analytics.trackEventOther({}, text.trim(), analtyicsType);
      }
    }
  };

  toggleAccordion = (event: MouseEvent | KeyboardEvent) => {
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      target.classList.toggle('open');
      const sibling = target.nextElementSibling as HTMLDivElement;
      const isCurrentlyExpanded = sibling.style.display === 'block';
      target.setAttribute(
        'aria-expanded',
        isCurrentlyExpanded ? 'false' : 'true',
      );
      sibling.style.display = isCurrentlyExpanded ? 'none' : 'block';
      // prevent scroll on spacebar press
      event.preventDefault();
    }
  };

  tabClick = (event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement | null;

    if (target) {
      const tabGroup = target.closest('.cg-tab-group');
      const tabPanel = target.getAttribute('aria-controls') || '';
      const tabPanelElemn = document.getElementById(tabPanel);

      tabGroup
        ?.querySelectorAll('.cg-tab-list button')
        .forEach((t: HTMLDivElement) =>
          t.setAttribute('aria-selected', 'false'),
        );
      target.setAttribute('aria-selected', 'true');

      tabGroup
        ?.querySelectorAll('.cg-tab-panel')
        .forEach((t: HTMLDivElement) => t.setAttribute('hidden', 'true'));
      if (tabPanelElemn) {
        tabPanelElemn.removeAttribute('hidden');
      }
    }
  };

  formatTableCell = (content: string) => {
    if (content === 'N') {
      return 'No';
    } else if (content === 'Y') {
      return 'Yes';
    }
    return content;
  };

  createTableFromApi = (table: HTMLTableElement) => {
    const apipath = table.dataset.tableApiPath;
    const responseObject = table.dataset.tableApiObject;

    if (apipath && responseObject) {
      fetch(apipath)
        .then((data) => data.json())
        .then((data) => {
          const rows = data[responseObject as string];

          for (const row of rows) {
            // insert at the end of the table
            const newRow = table.insertRow(-1);

            Object.values(row).forEach((r: string, i) => {
              const cell = newRow.insertCell(i);
              cell.textContent = this.formatTableCell(r);
            });
          }

          table.deleteRow(1);
        });
    }
  };

  tableSearch = (event: InputEvent) => {
    const target = event.currentTarget as HTMLInputElement | null;
    if (target) {
      const value = target.value || '';
      const table = target.closest('table.datatable');
      const tbody = table?.querySelector('tbody');
      const rows = tbody?.getElementsByTagName('tr') || [];
      const rowData = Array.from(rows);

      for (const [i, data] of rowData.entries()) {
        if (data.innerHTML.toUpperCase().includes(value.toUpperCase())) {
          rows[i].style.display = '';
        } else {
          rows[i].style.display = 'none';
        }
      }
    }
  };
}
